import React from "react"
import { createSelector } from "@reduxjs/toolkit"
import { useSearchSelector } from "./search-redux-context"
import { filtersSelectors } from "./store/filters-slice"
import { paginationSelectors } from "./store/pagination-slice"
import { SearchQuery } from "./store/search-thunk"
import useSearchContext from "./use-search-context"

const VERSION = "1"
const PREFIX = "search"
const VERSION_KEY = `${PREFIX}[v]`
const PAGE_KEY = `${PREFIX}[p]`
const SIZE_KEY = `${PREFIX}[s]`

type RangeValue = { from: string; to: string }
type SearchUrlQuery = Pick<SearchQuery, "filters" | "from" | "size">

const selectCurrentQuery = createSelector(
  [
    filtersSelectors.selectQueryFilters,
    paginationSelectors.selectQueryPagination,
  ],
  (filters, pagination): SearchUrlQuery => ({ filters, ...pagination }),
)

export type SearchUrlState = {
  filters: Record<string, string[]>
  ranges: Record<string, RangeValue[]>
  page: number | null
  size: number | null
}

const emptyState = (): SearchUrlState => ({
  filters: {},
  ranges: {},
  page: null,
  size: null,
})

const termKey = (id: string) => `${PREFIX}[f][${id}]`
const rangeKey = (id: string, index: number, boundary: "from" | "to") =>
  `${PREFIX}[r][${id}][${index}][${boundary}]`

function positiveInteger(value: string | null): number | null {
  if (value === null || !/^[1-9]\d*$/.test(value)) return null
  return Number(value)
}

export function decodeSearchUrlState(search: string): SearchUrlState {
  const params = new URLSearchParams(search)
  if (params.get(VERSION_KEY) !== VERSION) return emptyState()

  const state = emptyState()
  const ranges = new Map<string, Map<number, Partial<RangeValue>>>()

  for (const [key, value] of params) {
    const termMatch = /^search\[f\]\[([^\]]+)\]$/.exec(key)
    if (termMatch) {
      const id = termMatch[1]
      state.filters[id] ??= []
      state.filters[id].push(value)
      continue
    }

    const rangeMatch = /^search\[r\]\[([^\]]+)\]\[(\d+)\]\[(from|to)\]$/.exec(
      key,
    )
    if (rangeMatch) {
      const [, id, index, boundary] = rangeMatch
      const values = ranges.get(id) ?? new Map<number, Partial<RangeValue>>()
      const range = values.get(Number(index)) ?? {}
      range[boundary as keyof RangeValue] = value
      values.set(Number(index), range)
      ranges.set(id, values)
    }
  }

  for (const [id, values] of ranges) {
    const completeRanges = [...values.entries()]
      .sort(([left], [right]) => left - right)
      .flatMap(([, range]) =>
        range.from === undefined || range.to === undefined
          ? []
          : [{ from: range.from, to: range.to }],
      )
    if (completeRanges.length > 0) state.ranges[id] = completeRanges
  }

  state.page = positiveInteger(params.get(PAGE_KEY))
  state.size = positiveInteger(params.get(SIZE_KEY))
  return state
}

function isSearchParameter(key: string): boolean {
  return (
    key === VERSION_KEY ||
    key === PAGE_KEY ||
    key === SIZE_KEY ||
    key.startsWith(`${PREFIX}[f][`) ||
    key.startsWith(`${PREFIX}[r][`)
  )
}

export function encodeSearchUrlState(
  currentSearch: string,
  state: SearchUrlState,
): string {
  const params = new URLSearchParams(currentSearch)
  for (const key of [...params.keys()]) {
    if (isSearchParameter(key)) params.delete(key)
  }

  const hasSearchState =
    Object.keys(state.filters).length > 0 ||
    Object.keys(state.ranges).length > 0 ||
    state.page !== null ||
    state.size !== null
  if (!hasSearchState) return params.toString()

  params.append(VERSION_KEY, VERSION)
  for (const id of Object.keys(state.filters).sort()) {
    for (const value of state.filters[id]) params.append(termKey(id), value)
  }
  for (const id of Object.keys(state.ranges).sort()) {
    for (const [index, range] of state.ranges[id].entries()) {
      params.append(rangeKey(id, index, "from"), range.from)
      params.append(rangeKey(id, index, "to"), range.to)
    }
  }
  if (state.page !== null) params.append(PAGE_KEY, String(state.page))
  if (state.size !== null) params.append(SIZE_KEY, String(state.size))
  return params.toString().replaceAll("%5B", "[").replaceAll("%5D", "]")
}

function applyUrlState(
  state: SearchUrlState,
  previousFilterIds: Set<string>,
  filters: ReturnType<typeof useSearchContext>["filters"],
  pagination: ReturnType<typeof useSearchContext>["pagination"],
) {
  const nextFilterIds = new Set([
    ...Object.keys(state.filters),
    ...Object.keys(state.ranges),
  ])
  for (const id of new Set([...previousFilterIds, ...nextFilterIds])) {
    filters.setValues(id, state.filters[id] ?? state.ranges[id] ?? [])
  }
  pagination.setSize(state.size ?? 20)
  pagination.setPage(state.page ?? 1)
  return nextFilterIds
}

function stateFromQuery(query: SearchUrlQuery): SearchUrlState {
  const { filters, from, size } = query
  const state = emptyState()
  for (const [id, filter] of Object.entries(filters)) {
    const values = filter.values as unknown[]
    if (values.every(isRangeValue)) {
      state.ranges[id] = values as RangeValue[]
    } else {
      state.filters[id] = values.map(String)
    }
  }
  state.page = from === 0 ? null : from / size + 1
  state.size = size === 20 ? null : size
  return state
}

function mergePendingState(
  state: SearchUrlState,
  pendingState: SearchUrlState,
): SearchUrlState {
  const merged = {
    ...state,
    filters: { ...pendingState.filters, ...state.filters },
    ranges: { ...pendingState.ranges, ...state.ranges },
    page: state.page ?? pendingState.page,
    size: state.size ?? pendingState.size,
  }
  for (const id of Object.keys(state.filters)) delete pendingState.filters[id]
  for (const id of Object.keys(state.ranges)) delete pendingState.ranges[id]
  if (state.page === pendingState.page) pendingState.page = null
  if (state.size === pendingState.size) pendingState.size = null
  return merged
}

function isRangeValue(value: unknown): value is RangeValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "from" in value &&
    "to" in value
  )
}

export function SearchUrlState() {
  const { filters, pagination } = useSearchContext()
  const currentQuery = useSearchSelector(selectCurrentQuery)
  const [hydration] = React.useState(() => {
    const state = decodeSearchUrlState(window.location.search)
    return {
      state,
      filterIds: applyUrlState(state, new Set<string>(), filters, pagination),
    }
  })
  const appliedFilterIds = React.useRef(hydration.filterIds)
  const pendingState = React.useRef(hydration.state)

  React.useEffect(() => {
    const onPopState = () => {
      pendingState.current = decodeSearchUrlState(window.location.search)
      appliedFilterIds.current = applyUrlState(
        pendingState.current,
        appliedFilterIds.current,
        filters,
        pagination,
      )
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [filters, pagination])

  React.useEffect(() => {
    const url = new URL(window.location.href)
    const search = encodeSearchUrlState(
      url.search,
      mergePendingState(stateFromQuery(currentQuery), pendingState.current),
    )
    if (search === url.search.slice(1)) return
    url.search = search
    window.history.replaceState(null, "", url)
  }, [currentQuery])

  return null
}
